import { Link, useParams } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { PageHeader, QueryState } from '../components/page';
import {
  useCancelPracticeAgentJob,
  usePracticeAgent,
  usePracticeAgentBundles,
  usePracticeAgentJobs,
  useQueuePracticeAgentJob,
  useRevokePracticeAgent,
  useSetPracticeAgentBundlePin,
  useSetPracticeAgentChannel,
} from '../api/hooks';
import type {
  PracticeAgentDeviceDetail,
  PracticeAgentHeartbeat,
  PracticeAgentJob,
  PracticeAgentJobCreate,
} from '../api/types';
import {
  ConfirmDialog,
  ConnectionBadge,
  FOLDER_TEXT,
  Fact,
  Facts,
  JOB_TYPE_TEXT,
  LOW_DISK_BYTES,
  OPEN_JOB_STATUSES,
  Section,
  agoText,
  bytesText,
  control,
  dateText,
  frozenText,
  jobResultText,
  lastImportText,
  practiceErrorText,
  practiceLoadError,
  setupStepText,
  updateText,
} from './practice-agents';
import { SetupLinkDialog, SetupLinkList } from './PracticeSetupLinks';
import { PatientModelKeySection } from './PatientModelSettings';

export const DEFAULT_KEYWORDS = 'LVEF, NYHA, Ejektionsfraktion, NT-proBNP';

export function PracticeAgentRoutePage() {
  const { deviceId } = useParams({ from: '/practice-agents/$deviceId' });
  return <PracticeAgentDetailPage deviceId={deviceId} />;
}

export function PracticeAgentDetailPage({ deviceId }: { deviceId: string }) {
  const device = usePracticeAgent(deviceId);
  const response = (
    device.error as { response?: { status?: number; data?: { error?: unknown } } } | null
  )?.response;
  // Rudi's own 404 names the computer; a 404 without it means an older Rudi.
  const notFound =
    response?.status === 422 ||
    (response?.status === 404 && typeof response.data?.error === 'string');

  return (
    <div>
      <Link
        to="/practice-agents"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Practice computers
      </Link>
      <QueryState
        isLoading={device.isLoading}
        error={notFound ? null : practiceLoadError(device.error)}
      >
        {notFound ? (
          <p role="alert">
            Rudi does not know this practice computer. Return to Practice computers and pick it
            again.
          </p>
        ) : device.data ? (
          <DeviceView device={device.data} />
        ) : null}
      </QueryState>
    </div>
  );
}

function DeviceView({ device }: { device: PracticeAgentDeviceDetail }) {
  const revoke = useRevokePracticeAgent();
  const [confirming, setConfirming] = useState(false);
  const [linking, setLinking] = useState(false);
  const revoked = device.connection === 'revoked';
  const name = device.practiceName ?? 'Unnamed Practice';

  const confirmRevoke = async () => {
    try {
      await revoke.mutateAsync(device.id);
      toast.success('Practice computer revoked');
      setConfirming(false);
    } catch (error) {
      toast.error(practiceErrorText(error, 'Could not revoke the practice computer'));
    }
  };

  return (
    <>
      <PageHeader
        title={name}
        description={
          [device.tenantName, device.teamName].filter(Boolean).join(' · ') || 'No tenant'
        }
        actions={
          !revoked && (
            <>
              {device.connection === 'connected' &&
                device.lastHeartbeat?.liveViewEnabled !== false && (
                  <Button variant="outline" asChild>
                    <Link to="/practice-agents/$deviceId/live" params={{ deviceId: device.id }}>
                      Open live view
                    </Link>
                  </Button>
                )}
              <Button variant="destructive" onClick={() => setConfirming(true)}>
                Revoke
              </Button>
            </>
          )
        }
      />
      <div className="mb-8">
        <Facts label="Practice computer">
          <Fact label="State">
            <ConnectionBadge connection={device.connection} />
          </Fact>
          <Fact label="Last seen">
            {device.lastSeenAt
              ? `${agoText(device.lastSeenAt)} (${dateText(device.lastSeenAt)})`
              : 'No heartbeat yet'}
          </Fact>
          <Fact label="Platform">
            {device.platform} {device.osVersion}
          </Fact>
          <Fact label="App version">{device.appVersion}</Fact>
          <Fact label="Bundle version">{device.bundleVersion ?? 'None'}</Fact>
          <Fact label="Protocol">{device.protocolVersion}</Fact>
          <Fact label="Build">{device.buildEnvironment}</Fact>
          <Fact label="Connected">{dateText(device.enrolledAt)}</Fact>
          {device.revokedAt && <Fact label="Revoked">{dateText(device.revokedAt)}</Fact>}
          <Fact label="Device key">
            <code className="text-xs">{device.keyId}</code>
          </Fact>
          <Fact label="Pre-screening">
            <Link
              to="/prescreening/practices/$practiceId"
              params={{ practiceId: device.practiceId }}
              className="underline underline-offset-4"
            >
              Code sets, criteria queries and runs
            </Link>
          </Fact>
        </Facts>
      </div>

      <Section
        title="Updates"
        description="Pilot gets app releases and engine bundles before stable. A pinned computer keeps its bundle until you remove the pin."
      >
        <UpdateSettings device={device} disabled={revoked} />
      </Section>

      <Section title="Self-check" description="What the app found at its last check.">
        <SelfCheck heartbeat={device.lastHeartbeat} />
      </Section>

      <Section title="Last heartbeat">
        <HeartbeatFacts device={device} />
      </Section>

      <Section
        title="Jobs"
        description="Results hold counts, codes and months only. The app and Rudi both check them."
      >
        <JobQueue device={device} disabled={revoked} />
      </Section>

      <Section
        title="Patient model key"
        description="This computer's own key for the patient model provider, which reads record text for stage 3 criteria."
      >
        <PatientModelKeySection device={device} />
      </Section>

      <Section
        title="Setup links"
        description="A new link connects another computer to this Practice, for example after a revoke."
        actions={
          <Button size="sm" variant="outline" onClick={() => setLinking(true)}>
            Create setup link
          </Button>
        }
      >
        <SetupLinkList practiceId={device.practiceId} />
      </Section>

      {linking && (
        <SetupLinkDialog
          practice={{
            id: device.practiceId,
            name,
            tenantId: device.tenantId,
            teamId: device.teamId,
          }}
          open
          showLinks={false}
          onClose={() => setLinking(false)}
        />
      )}

      <ConfirmDialog
        open={confirming}
        title="Revoke practice computer"
        description={`${name} stops getting jobs and updates at once, and the app shows that it was disconnected. It keeps its local store until someone uninstalls it. Connecting it again needs a new setup link.`}
        confirmLabel={revoke.isPending ? 'Revoking…' : 'Revoke computer'}
        pending={revoke.isPending}
        destructive
        onConfirm={() => void confirmRevoke()}
        onClose={() => setConfirming(false)}
      />
    </>
  );
}

function UpdateSettings({
  device,
  disabled,
}: {
  device: PracticeAgentDeviceDetail;
  disabled: boolean;
}) {
  const bundles = usePracticeAgentBundles();
  const setChannel = useSetPracticeAgentChannel();
  const setPin = useSetPracticeAgentBundlePin();
  const channels = ['stable', 'pilot'].includes(device.channel)
    ? ['stable', 'pilot']
    : ['stable', 'pilot', device.channel];
  const pinnable = (bundles.data ?? []).filter(
    bundle => !bundle.yankedAt || bundle.version === device.pinnedBundleVersion
  );
  const pinListed =
    !device.pinnedBundleVersion ||
    pinnable.some(bundle => bundle.version === device.pinnedBundleVersion);

  const changeChannel = async (channel: string) => {
    try {
      await setChannel.mutateAsync({ deviceId: device.id, channel });
      toast.success(`Channel set to ${channel}`);
    } catch (error) {
      toast.error(practiceErrorText(error, 'Could not change the channel'));
    }
  };

  const changePin = async (version: string) => {
    try {
      await setPin.mutateAsync({ deviceId: device.id, version: version || null });
      toast.success(version ? `Pinned to bundle ${version}` : 'Bundle pin removed');
    } catch (error) {
      toast.error(practiceErrorText(error, 'Could not change the bundle pin'));
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-4 rounded-3xl border bg-card p-4">
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Channel
        <select
          aria-label="Channel"
          className={control}
          value={device.channel}
          disabled={disabled || setChannel.isPending}
          onChange={event => void changeChannel(event.target.value)}
        >
          {channels.map(channel => (
            <option key={channel} value={channel}>
              {channel === 'stable' ? 'Stable' : channel === 'pilot' ? 'Pilot' : channel}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Bundle pin
        <select
          aria-label="Bundle pin"
          className={control}
          value={device.pinnedBundleVersion ?? ''}
          disabled={disabled || setPin.isPending}
          onChange={event => void changePin(event.target.value)}
        >
          <option value="">None, newest on its channel</option>
          {!pinListed && device.pinnedBundleVersion && (
            <option value={device.pinnedBundleVersion}>{device.pinnedBundleVersion}</option>
          )}
          {pinnable.map(bundle => (
            <option key={bundle.id} value={bundle.version}>
              {bundle.version}
              {bundle.yankedAt ? ' (yanked)' : ` (${bundle.channels.join(', ')})`}
            </option>
          ))}
        </select>
      </label>
      {bundles.data === null && (
        <p className="text-xs text-muted-foreground">
          This Rudi release lists no engine bundles to pin.
        </p>
      )}
    </div>
  );
}

function SelfCheck({ heartbeat }: { heartbeat: PracticeAgentHeartbeat | null }) {
  const check = heartbeat?.selfCheck;
  if (!check) return <p className="text-sm text-muted-foreground">No self-check reported yet.</p>;
  const bad = 'text-destructive';
  const folder = (state: keyof typeof FOLDER_TEXT) => (
    <span className={state === 'ok' ? '' : bad}>{FOLDER_TEXT[state] ?? state}</span>
  );
  const disk = (bytes: number | null) => (
    <span className={bytes !== null && bytes < LOW_DISK_BYTES ? bad : ''}>
      {bytes === null ? 'Unknown' : `${bytesText(bytes)} free`}
    </span>
  );
  return (
    <Facts label="Self-check">
      <Fact label="Rudi host">
        <span className={check.hostReachable ? '' : bad}>
          {check.hostReachable ? 'Reachable' : 'Unreachable'}
        </span>
      </Fact>
      <Fact label="Patient model">
        <span className={check.patientModelReachable === false ? bad : ''}>
          {check.patientModelReachable === null
            ? 'Not in use'
            : check.patientModelReachable
              ? 'Reachable'
              : 'Unreachable'}
        </span>
      </Fact>
      <Fact label="Export folder">{folder(check.exportFolder)}</Fact>
      <Fact label="Rudi folder">{folder(check.rudiFolder)}</Fact>
      <Fact label="Local disk">{disk(check.localFreeBytes)}</Fact>
      <Fact label="Share">{disk(check.shareFreeBytes)}</Fact>
    </Facts>
  );
}

function HeartbeatFacts({ device }: { device: PracticeAgentDeviceDetail }) {
  const heartbeat = device.lastHeartbeat;
  if (!heartbeat) return <p className="text-sm text-muted-foreground">No heartbeat yet.</p>;
  const plan = heartbeat.exportPlan;
  const frozen = frozenText(heartbeat);
  return (
    <Facts label="Last heartbeat">
      <Fact label="Received">{dateText(heartbeat.receivedAt)}</Fact>
      <Fact label="Setup step">{setupStepText(heartbeat.setupStep)}</Fact>
      <Fact label="Reported channel">{heartbeat.reportedChannel ?? 'Unknown'}</Fact>
      <Fact label="Frozen">{frozen.length ? frozen.join(', ') : 'Nothing'}</Fact>
      <Fact label="App updates">{updateText(heartbeat) ?? 'Nothing pending'}</Fact>
      <Fact label="Last import">
        <span className={heartbeat.lastImport?.status === 'failed' ? 'text-destructive' : ''}>
          {lastImportText(heartbeat)}
        </span>
      </Fact>
      <Fact label="Export plan">
        {plan
          ? `Day ${plan.monthlyDay} of each month, each export covers ${plan.windowMonths} months`
          : 'Not set'}
      </Fact>
      <Fact label="Next export due">
        {heartbeat.nextExportDueOn ?? plan?.nextDueOn ?? 'Unknown'}
      </Fact>
      <Fact label="Last backup">{dateText(heartbeat.lastBackupAt)}</Fact>
      <Fact label="Local store">{bytesText(heartbeat.storeBytes)}</Fact>
      <Fact label="Jobs waiting">{heartbeat.jobsWaiting ?? 'Unknown'}</Fact>
      <Fact label="Last error">
        {heartbeat.lastErrorClass ? (
          <span className="text-destructive">{heartbeat.lastErrorClass}</span>
        ) : (
          'None'
        )}
      </Fact>
      <Fact label="Live view">
        {heartbeat.liveViewEnabled === null ? 'Unknown' : heartbeat.liveViewEnabled ? 'On' : 'Off'}
      </Fact>
    </Facts>
  );
}

/** Keywords from a comma-separated list, trimmed and without repeats. */
export function parseKeywords(text: string): string[] {
  return [
    ...new Set(
      text
        .split(',')
        .map(word => word.trim())
        .filter(Boolean)
    ),
  ];
}

function JobQueue({ device, disabled }: { device: PracticeAgentDeviceDetail; disabled: boolean }) {
  const jobs = usePracticeAgentJobs(device.id);
  const queue = useQueuePracticeAgentJob();
  const cancel = useCancelPracticeAgentJob();
  const [keywords, setKeywords] = useState(DEFAULT_KEYWORDS);
  const parsed = parseKeywords(keywords);
  const tooLong = parsed.find(word => word.length > 64);

  const send = async (job: PracticeAgentJobCreate, label: string) => {
    try {
      await queue.mutateAsync({ deviceId: device.id, job });
      toast.success(`${label} queued`);
    } catch (error) {
      toast.error(practiceErrorText(error, `Could not queue the ${label.toLowerCase()}`));
    }
  };

  const queueStructure = (event: FormEvent) => {
    event.preventDefault();
    if (tooLong || parsed.length > 200) return;
    void send(
      { type: 'report.structure', typeVersion: 1, payload: { keywords: parsed } },
      'Structure report'
    );
  };

  const cancelJob = async (job: PracticeAgentJob) => {
    try {
      await cancel.mutateAsync(job.id);
      toast.success('Job cancelled');
    } catch (error) {
      toast.error(practiceErrorText(error, 'Could not cancel the job'));
    }
  };

  return (
    <div className="space-y-4">
      {!disabled && (
        <div className="flex flex-wrap items-end gap-3 rounded-3xl border bg-card p-4">
          <form className="flex flex-1 flex-wrap items-end gap-3" onSubmit={queueStructure}>
            <label className="flex min-w-64 flex-1 flex-col gap-1 text-xs text-muted-foreground">
              Keywords to count in free text, separated by commas
              <Input
                aria-label="Keywords"
                value={keywords}
                onChange={event => setKeywords(event.target.value)}
              />
              {tooLong && (
                <span className="text-destructive">Keep each keyword to 64 characters.</span>
              )}
            </label>
            <Button type="submit" disabled={queue.isPending || !!tooLong || parsed.length > 200}>
              Queue structure report
            </Button>
          </form>
          <Button
            variant="outline"
            disabled={queue.isPending}
            onClick={() =>
              void send({ type: 'import.reparse', typeVersion: 1, payload: {} }, 'Re-import')
            }
          >
            Queue re-import
          </Button>
        </div>
      )}
      <QueryState isLoading={jobs.isLoading} error={practiceLoadError(jobs.error)}>
        <div className="overflow-x-auto rounded-3xl border">
          <Table aria-label="Jobs">
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Origin</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Delivered</TableHead>
                <TableHead>Result</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(jobs.data ?? []).map(job => (
                <TableRow key={job.id}>
                  <TableCell>
                    <div className="font-medium">{JOB_TYPE_TEXT[job.type] ?? job.type}</div>
                    <div className="text-xs text-muted-foreground">
                      {job.type} v{job.typeVersion}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        job.status === 'succeeded'
                          ? 'default'
                          : job.status === 'failed' || job.status === 'rejected'
                            ? 'destructive'
                            : 'outline'
                      }
                    >
                      {job.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{job.origin}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {dateText(job.createdAt)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {job.deliveredAt
                      ? `${dateText(job.deliveredAt)}${job.deliveryCount > 1 ? ` (${job.deliveryCount} times)` : ''}`
                      : 'Not yet'}
                  </TableCell>
                  <TableCell className="min-w-[14rem] text-xs">
                    <div>{jobResultText(job)}</div>
                    {job.result && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-muted-foreground">
                          Result JSON
                        </summary>
                        <pre className="mt-1 max-h-64 max-w-xl overflow-auto rounded-3xl bg-muted p-3 text-[11px]">
                          {JSON.stringify(job.result, null, 2)}
                        </pre>
                      </details>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {OPEN_JOB_STATUSES.has(job.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={`Cancel ${JOB_TYPE_TEXT[job.type] ?? job.type}`}
                        disabled={cancel.isPending}
                        onClick={() => void cancelJob(job)}
                      >
                        Cancel
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {jobs.data?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="h-20 text-center text-muted-foreground">
                    No jobs yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </QueryState>
    </div>
  );
}
